import { acompanantesService } from '../acompanantes'
import { ServiceError, unsupported } from '../errors'
import { pmsUrl, postJson } from '../http'
import type { CatalogOption } from '../reserva/types'
import { readLocal } from '../local-store'
import { featureEnabled } from '@/config/features'
import { ARRIVAL_FIELD, checkinMock } from './mock'
import { buildOccupants } from './occupants'
import type { Arrival, CheckinService, GuestData, SavedGuest, StayArg } from './types'
import type { Stay } from '../reserva/types'

/** Respuesta de `savetravelregister_guestapp` (copiada de src/api/savetravelregister_guestapp de la app actual). */
export interface ApiSaveTravelRegisterResponse {
  travelRegistrationId: string
  photoFacePath?: string
  documentList?: { name: string; url: string; documentType: 'PDF' | 'HTML' }[]
  ok: boolean
  status?: string
  error?: string
}

/** `yyyy-MM-dd` → `dd-MM-yyyy` */
export const isoDateToPms = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? `${m[3]}-${m[2]}-${m[1]}` : iso
}

const today = (now = new Date()) => `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`
const opt = (s: string) => (s.trim() === '' ? undefined : s.trim())

/**
 * Cuerpo de E5 con los datos de una persona. Replica apiSaveTravelRegister de la app actual:
 * `provinceId`/`municipalityId` por id, `expeditionDate` = hoy, `verifiedData: true`, fotos y `rawData`
 * solo si existen, y `relationship` para menores con adulto responsable.
 */
export function toSaveBody(stay: StayArg & Pick<Stay, 'registration'>, guest: GuestData, lang: string, extra: { travelRegistrationId?: string; photoFront?: string; photoBack?: string; photoFace?: string; rawData?: unknown } = {}, now = new Date()) {
  const rel = guest.relationship
  const adult = rel ? stay.registration.travelers.find((tr) => tr.id === rel.responsibleAdultId) : undefined
  return {
    reserveId: stay.id,
    name: guest.name.trim(),
    surname1: guest.surname1.trim(),
    surname2: guest.surname2.trim(),
    identifier: guest.identifier.trim(),
    identifierTypeId: guest.identifierTypeId,
    supportNumber: opt(guest.supportNumber.toUpperCase()),
    bornDate: isoDateToPms(guest.bornDate),
    sexId: guest.sexId,
    email: guest.email.trim(),
    countryId: guest.countryId,
    provinceId: opt(guest.provinceId),
    municipalityId: opt(guest.municipalityId),
    city: opt(guest.city),
    address: guest.address.trim(),
    postalCode: guest.postalCode.trim(),
    userLang: lang,
    expeditionDate: today(now),
    verifiedData: true,
    travelRegistrationId: extra.travelRegistrationId,
    ...(rel && adult && rel.childRelationshipId && rel.adultRelationshipId
      ? {
          relationship: {
            idOfAdult: adult.id,
            idOfJunior: extra.travelRegistrationId,
            identifierOfAdult: adult.details.identifier ?? '',
            identifierOfJunior: guest.identifier.trim(),
            relationshipOfAdult: rel.adultRelationshipId,
            relationshipOfJunior: rel.childRelationshipId,
          },
        }
      : {}),
    ...(extra.photoFront ? { photoFront: extra.photoFront } : {}),
    ...(extra.photoBack ? { photoBack: extra.photoBack } : {}),
    ...(extra.photoFace ? { photoFace: extra.photoFace } : {}),
    ...(extra.rawData ? { rawData: extra.rawData } : {}),
  }
}

async function postSave(stay: StayArg, body: object): Promise<ApiSaveTravelRegisterResponse> {
  const data = await postJson<ApiSaveTravelRegisterResponse>(pmsUrl(stay.serverPath, 'savetravelregister_guestapp'), body, { auth: 'web' })
  if (!data.ok) throw new ServiceError('server', data.error, data.status)
  return data
}

const toSaved = (data: ApiSaveTravelRegisterResponse): SavedGuest => ({
  travelerId: data.travelRegistrationId,
  documents: (data.documentList ?? []).map((d) => ({ name: d.name, url: d.url, type: d.documentType })),
})

/**
 * Registro de viajeros contra la API actual: E5 (`savetravelregister_guestapp`, dos llamadas: datos y
 * firma, como hoy) y E8 (`getmunicipalityofprovince`, sin autenticación). Lo que no tiene endpoint
 * (llegada, entrada anticipada, móvil, resumen de normas, envío del contrato) delega en el mock (D29).
 */
export const checkinReal: CheckinService = {
  notices: { phoneReachesProperty: false, contractByEmail: false },
  // Delegan en el mock para guardar en el dispositivo, pero sin sus datos de ejemplo: una reserva real
  // nunca muestra la llegada de Marta, una entrada anticipada inventada ni normas que no son de la propiedad.
  getArrival: (stay) => Promise.resolve(readLocal<Arrival>(stay.guestCode, ARRIVAL_FIELD)),
  setArrival: (stay, input) => checkinMock.setArrival(stay, input),
  getArrivalOptions: async (stay, signal) => ({ ...(await checkinMock.getArrivalOptions(stay, signal)), earlyCheckin: null }),
  getPhone: (stay) => checkinMock.getPhone(stay),
  setPhone: (stay, phone) => checkinMock.setPhone(stay, phone),
  getContractSummary: () => unsupported(),
  // Sin endpoint no se envía nada: decir "te hemos enviado el contrato" sería falso (fase 4, A3; afina D29).
  sendContractBeforeSigning: () => unsupported(),

  async getOccupants(stay, signal) {
    if (!featureEnabled('invitados')) return buildOccupants(stay, [], {}, [])
    // Las invitaciones son pospuestas: si su servicio falla, la lista sigue con los datos reales.
    const [invitations, revoked] = await Promise.all([acompanantesService.list(stay, signal).catch(() => []), acompanantesService.listRevoked(stay, signal).catch(() => [])])
    return buildOccupants(stay, invitations, {}, revoked)
  },

  async getMunicipalities(stay, provinceId, signal) {
    const data = await postJson<{ municipalityList?: { id: string; value: string }[] }>(pmsUrl(stay.serverPath, 'getmunicipalityofprovince'), { id: provinceId }, { auth: 'none', ...(signal ? { signal } : {}) })
    return (data.municipalityList ?? []).map((m): CatalogOption => ({ id: m.id, name: m.value, value: m.value }))
  },

  async saveGuest(stay, guest, attachments, rawData, lang) {
    const body = toSaveBody(stay, guest, lang, { ...(attachments.front ? { photoFront: attachments.front } : {}), ...(attachments.back ? { photoBack: attachments.back } : {}), ...(attachments.face ? { photoFace: attachments.face } : {}), rawData })
    return toSaved(await postSave(stay, body))
  },

  async completeGuest(stay, travelerId, guest, lang) {
    return toSaved(await postSave(stay, toSaveBody(stay, guest, lang, { travelRegistrationId: travelerId })))
  },

  // Mismo cuerpo que la segunda llamada de hoy (SignContractForm): solo id, firma y los dos campos fijos.
  async saveSignature(stay, travelerId, signaturePng) {
    await postSave(stay, { reserveId: stay.id, travelRegistrationId: travelerId, photoSignature: signaturePng, expeditionDate: today(), verifiedData: true })
  },
}
